import React from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { Url, sanitizeHref } from '@wix/da-url';
import ExclusiveButton from '@wix/da-ds/pkg/Buttons/ExclusiveButton';
import DownloadIcon from '@wix/da-ds/pkg/Icons/24x24/Download';
import VideoCreatorIcon from '@wix/da-ds/pkg/Icons/VideoCreator';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { BiData, ClickCreateAiVideoBiEvent } from '@wix/da-bi/pkg/events';
import { isSafeForMuroAndVideoCreator } from '../../../../../../ActionBar/ElementPlacementContext/utils';
import useAdoptableState from '../../../AdoptableInfo/useAdoptableState';
import { useExclusiveCategoryTranslation, useResellAction } from '../../hooks';
import Card from '../../lego/Card';
import CardHeading from '../../lego/CardHeading';
import Stack from '../../lego/Stack';
import OwnerRow from '../../lego/OwnerRow';
import ResaleIndicator from '../../lego/ResaleIndicator';
import TradeCount from '../../lego/TradeCount';

import s from './AdoptableSold.scss';

function getSellPrice(deviation: PapiDeviation): number | undefined {
  const { fixedPrice, offers } = deviation.adoptable ?? {};
  if (offers) {
    return offers.offerPrice;
  } else if (fixedPrice) {
    return fixedPrice.fixedPrice;
  }
}

export interface Props {
  deviation: PapiDeviation;
  className?: string;
}

/**
 * An adoptable specific variant of our exclusive sidebar card. Used only when the exclusive has been sold and is not on sale.
 *
 * NOTE: This component is used for all exclusive types as of this comment's writing. There is no difference spec'd between
 * these cards. Because I don't believe this will stay the case, I architected this in a way to make it easy to make a brand-new
 * sidebar card component.
 *
 * If an exclusive category's card features start to diverge, make a new component instead of putting it in here.
 */
const AdoptableSold: React.FC<Props> = ({ deviation, className }) => {
  const { t } = useTranslation();
  const exclusiveCategoryName = useExclusiveCategoryTranslation(deviation);
  const adoptableState = useAdoptableState(deviation);
  const { isResellOptionEnabled, isSeller, saleCount } = adoptableState;
  const [resellAction, resellBiData] = useResellAction(deviation, adoptableState);

  const { isOriginalsDelivery } = deviation;
  const { isOwner, assetSourceUrl, owner, lastTradeTs, wasGifted } =
    deviation.extended?.adoptable ?? {};
  const downloadLink = isOwner && assetSourceUrl;
  const sellPrice = getSellPrice(deviation);
  const showCreateVideo = isOwner && isSafeForMuroAndVideoCreator(deviation);

  return (
    <Card badgeType="adoptable" className={className}>
      <Stack gap={16}>
        <CardHeading deviation={deviation}>{exclusiveCategoryName}</CardHeading>

        {owner && lastTradeTs && (
          <OwnerRow
            user={owner}
            sellPrice={sellPrice}
            sellTimestamp={lastTradeTs}
            wasGifted={wasGifted}
            isOriginalsDelivery={isOriginalsDelivery}
          />
        )}

        {isSeller && (
          <ResaleIndicator isResellable={!!deviation.extended?.adoptable?.resaleAllowed} />
        )}

        {!isSeller && !!saleCount && (
          <TradeCount deviation={deviation} count={saleCount} className={s['trade-count']} />
        )}

        {downloadLink && (
          <ExclusiveButton priority="secondary" href={sanitizeHref(downloadLink)}>
            <DownloadIcon
              size={IconSize.MEDIUM}
              disableDefaultColors
              className={s['download-icon']}
            />
            {t('sidebar.exclusives.buttons.download')}
          </ExclusiveButton>
        )}

        {showCreateVideo && (
          <ExclusiveButton
            priority="secondary"
            href={Url.videoCreatorLink({
              deviation,
            })}
            biData={BiData<ClickCreateAiVideoBiEvent>({
              evid: 952,
              typeid: 1,
              itemid: deviation.deviationId,
            })}
          >
            <VideoCreatorIcon
              size={IconSize.MEDIUM}
              disableDefaultColors
              className={s['create-video-icon']}
            />
            {t('sidebar.exclusives.buttons.video')}
          </ExclusiveButton>
        )}

        {isResellOptionEnabled && (
          <ExclusiveButton
            priority="primary"
            onClick={resellAction}
            disabled={!resellAction}
            biData={resellBiData}
          >
            {t('sidebar.exclusives.buttons.sell')}
          </ExclusiveButton>
        )}
      </Stack>
    </Card>
  );
};

AdoptableSold.displayName = 'AdoptableSold';

export default AdoptableSold;
